<template>
  <v-dialog
    v-model="dialog"
    content-class="custom-dialog"
    transition="slide-x-transition"
    persistent
  >
    <v-card
      class="absolute overflow-hidden"
      height="100%"
      :width="xs || sm ? '100%' : 500"
      rounded="0"
    >
      <v-card-title class="font-weight-bold px-6">Данные пользователя</v-card-title>
      <v-card-text class="dialog-content">
        <template v-if="!isEdit">
          <UserFormSection
            v-for="section in viewSections"
            :key="section.title"
            :icon="section.icon"
            :title="section.title"
            class="user-info-section"
          >
            <v-row
              v-for="item in section.items"
              :key="item.label"
              class="ma-0 w-100 flex-column flex-md-row align-start align-md-center"
            >
              <v-col class="view-label text-grey col-12" md="6">{{ item.label }}</v-col>
              <v-col class="view-value col-12" md="6">{{ item.value }}</v-col>
            </v-row>
          </UserFormSection>
        </template>
        <UserForm
          v-else
          v-model="changeData"
          submit-label="Сохранить"
          :loading="isLoading"
          :show-cancel="true"
          :compact-buttons="true"
          email-disabled
          @submit="saveUserInfo"
          @cancel="toggleEditMode"
        />
        <!-- в режиме редактирования блок уведомлений скрыт: форма и так длинная,
             а настройки не относятся к редактируемым данным пользователя -->
        <v-col v-if="!isEdit" class="px-0 mt-md-4 mt-2">
          <h3 class="my-2 my-dm-6 subtitle-1">Настройка уведомлений</h3>
          <v-row v-if="isLoading" class="px-0 justify-center align-center mt-12">
            <v-progress-circular color="primary" :indeterminate="true" />
          </v-row>
          <template v-else>
            <div v-for="(field, index) in fieldsForm" :key="index">
              <v-col class="align-center pa-0">
                <p v-if="field.title" class="text-body-2 ma-0 pa-0 text-grey">
                  {{ field.title }}
                </p>
                <Component
                  :is="fieldsInputs[getFormInput(field.type)]"
                  v-bind="field"
                  @update-value="updateValue"
                />
              </v-col>
            </div>
          </template>
        </v-col>
      </v-card-text>
      <v-card-actions class="justify-center align-start py-4">
        <v-btn v-if="!isLoading" class="flex-grow-1" color="primary" variant="text" @click="close">
          Закрыть
        </v-btn>
        <v-btn
          v-if="!isEdit && !isLoading"
          class="flex-grow-1"
          color="primary"
          variant="elevated"
          @click="editUserInfo"
        >
          Изменить
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useStore } from 'vuex'
import { v4 as uuidv4 } from 'uuid'

import { api } from '@/api/Api'
import { useToast } from '@/composables/useToast'

import FormRadio from '@/components/Form/FormRadio'
import UserForm from '@/components/User/UserForm.vue'
import UserFormSection from '@/components/User/UserFormSection.vue'
import {
  formToProfilePatch,
  fromIsoBirthDate,
  genderInString,
  userInfoToForm,
} from '@/utils/userForm'
import { useDisplay } from 'vuetify'

const store = useStore()
const { toast } = useToast()
const { sm, xs } = useDisplay()

const fieldsInputs = {
  FormRadio,
}

const fieldsForm = ref([
  {
    id: uuidv4(),
    group_id: 0,
    type: 'radio',
    name: 'send_email',
    title: 'Email-уведомления',
    options: [
      {
        id: 7,
        name: 'Вкл',
        value: 1,
        description: '',
        disabled: 'N',
        sort: 0,
      },
      {
        id: 8,
        name: 'Выкл',
        value: 0,
        description: '',
        disabled: 'N',
        sort: 1,
      },
    ],
    default_value: 0,
    descr: '',
    note: '',
    multiple: 0,
    rules: [],
    sort: 1,
  },
])

const userInfo = computed(() => {
  return store.getters['user/userInfo']
})

const userRole = computed(() => store.getters['user/userRoleInString'])

// незаполненные данные в режиме просмотра показываем прочерком
const emptyToDash = (v) => (v === '' || v === null || v === undefined ? '—' : v)

// просмотр данных — те же смысловые группы, что и в форме редактирования (UserForm)
const viewSections = computed(() => [
  {
    icon: 'pi pi-user',
    title: 'Личные данные',
    items: [
      {
        label: 'Фамилия',
        value: emptyToDash(userInfo.value.lastname),
      },
      {
        label: 'Имя',
        value: emptyToDash(userInfo.value.firstname),
      },
      {
        label: 'Отчество',
        value: emptyToDash(userInfo.value.middlename),
      },
      {
        label: 'Пол',
        value: emptyToDash(genderInString(userInfo.value.gender)),
      },
      {
        label: 'Дата рождения',
        value: emptyToDash(fromIsoBirthDate(userInfo.value.birthday)),
      },
    ],
  },
  {
    icon: 'pi pi-id-card',
    title: 'Контакты',
    items: [
      {
        label: 'E-mail',
        value: emptyToDash(userInfo.value.email),
      },
      {
        label: 'Телефон',
        value: emptyToDash(userInfo.value.phone),
      },
    ],
  },
  {
    icon: 'pi pi-map-marker',
    title: 'Адрес',
    items: [
      {
        label: 'Регион',
        value: emptyToDash(userInfo.value.region),
      },
      {
        label: 'Населённый пункт',
        value: emptyToDash(userInfo.value.city),
      },
    ],
  },
  {
    icon: 'pi pi-briefcase',
    title: 'Работа',
    items: [
      {
        label: 'Организация',
        value: emptyToDash(userInfo.value.work_company),
      },
      {
        label: 'Должность',
        value: emptyToDash(userInfo.value.work_position),
      },
    ],
  },
  {
    icon: 'pi pi-shield',
    title: 'Система',
    items: [
      {
        label: 'Роль',
        value: emptyToDash(userRole.value),
      },
      {
        label: 'Идентификатор',
        value: emptyToDash(userInfo.value.id),
      },
    ],
  },
])
const dialog = ref(false)
const isEdit = ref(false)
const changeData = ref({})
const isLoading = ref(false)
const settings = ref(null)

function getFormInput(type) {
  switch (type) {
    case 'radio': {
      return 'FormRadio'
    }
    default: {
      return ''
    }
  }
}

function open() {
  isEdit.value = false
  dialog.value = true
  getUserSetttings()
}

function close() {
  dialog.value = false
}

function toggleEditMode() {
  isEdit.value = !isEdit.value
}

function editUserInfo() {
  changeData.value = userInfoToForm(userInfo.value)
  toggleEditMode()
}

function updateValue({ value, id }) {
  fieldsForm.value.forEach((item) => {
    if (item.id === id) {
      item.default_value = value
    }
  })
  const params = {}
  fieldsForm.value.forEach((el) => {
    params[el.name] = el.default_value
  })

  sendNewSettinsNotifications(params)
}

async function sendNewSettinsNotifications(params) {
  isLoading.value = true
  try {
    await api.notifications().changeNotificationsStatus(params)
    toast.success('Настройки уведомлений успешно обновлены')
  } catch (e) {
    toast.error('Произошла ошибка при обновлении настроек уведомлений')
  } finally {
    isLoading.value = false
  }
}

async function saveUserInfo() {
  isLoading.value = true
  try {
    await api.user().updateProfile(formToProfilePatch(changeData.value))
    toast.success('Данные успешно обновлены')
    toggleEditMode()
    await store.dispatch('user/getUserInfo')
  } catch (e) {
    if (e?.response?.status === 400 && e?.response?.data?.error) {
      toast.error(e.response.data.error)
    } else {
      toast.error('Произошла ошибка при обновлении данных')
    }
  } finally {
    isLoading.value = false
  }
}

function setDefaultValueSettings() {
  fieldsForm.value = fieldsForm.value.map((el) => {
    const newVal = settings.value ? +settings.value[el.name] : 0
    el.default_value = newVal
    return el
  })
}

async function getUserSetttings() {
  isLoading.value = true

  try {
    settings.value = await api.notifications().getSettingsNotifications()
    setDefaultValueSettings()
  } catch (e) {
    toast.error('Произошла ошибка при получении данных')
  } finally {
    isLoading.value = false
  }
}

defineExpose({
  open,
})
</script>

<style lang="scss" scoped>
.user-info-section {
  // те же размеры шрифтов, что и в форме редактирования (UserForm):
  // значения — 14px (как поля ввода), метки — 13px
  font-size: 14px;
  line-height: 20px;

  // глобальное .v-row { font-size: 12px } из main.scss перекрывает
  // унаследованный размер — переопределяем его здесь
  .v-row {
    font-size: 14px;
    line-height: 20px;
  }

  // вертикальные зазоры между строками уменьшены:
  // оставлен только gap секции (10px), внутренние паддинги колонок убраны
  .v-col {
    padding: 2px 0;
  }

  .view-label {
    padding-right: 8px;
    font-size: 13px;

    @media (min-width: 960px) {
      padding: 2px 8px 2px 0;
    }
  }

  .view-value {
    word-break: break-word;
  }
}

.user-info-section + .user-info-section {
  margin-top: 20px;
}
</style>
